import { AppModal } from '@/components/ui/app-modal';
import { useState } from 'react';
import { useI18n } from '@/i18n';
import { useTenant } from '@/contexts/TenantContext';
import {
  Badge,
  Button,
  Card,
  Input,
  Spinner,
} from '@/components/ui';
import {
  useCACKycChecks,
  useCACPartnerProfile,
  useCACRequestTierUpgrade,
  useCACSubmitKycEvidence,
  useCACTierTransitions,
  useCACUpdatePartnerProfile,
} from '@/features/cac';
import type { KycCheck, PartnerTier } from '@/features/cac';

const TIER_BADGE_CLASS: Record<PartnerTier, string> = {
  verified: 'bg-blue-50 text-blue-700 border-blue-200',
  premium: 'bg-amber-50 text-amber-700 border-amber-200',
  enterprise: 'bg-purple-50 text-purple-700 border-purple-200',
};

const KYC_STATUS_BADGE_CLASS: Record<string, string> = {
  pending: 'bg-gray-50 text-gray-600 border-gray-200',
  in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
  approved: 'bg-green-50 text-green-700 border-green-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
  expired: 'bg-amber-50 text-amber-700 border-amber-200',
};

const KYC_SLUG_LABELS: Record<string, string> = {
  email_verification: 'Email Verification',
  fiscal_profile: 'Fiscal Profile (Tax ID)',
  identity_verification: 'Identity Verification',
  business_registration: 'Business Registration',
  contract_signing: 'Contract Signing',
  enterprise_agreement: 'Enterprise Agreement',
};

function publicErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object') return 'Operation failed.';
  const maybeAxios = error as {
    response?: { data?: { detail?: { message?: string } } };
  };
  return maybeAxios.response?.data?.detail?.message ?? 'Operation failed. Please retry.';
}

export function PartnerProfilePage() {
  const { t: _t } = useI18n();
  void _t;
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [evidenceModal, setEvidenceModal] = useState<string | null>(null);
  const [evidenceType, setEvidenceType] = useState('document');
  const [evidenceTaxId, setEvidenceTaxId] = useState('');
  const [evidenceCountry, setEvidenceCountry] = useState('');
  const [evidenceNotes, setEvidenceNotes] = useState('');

  const profileQuery = useCACPartnerProfile(tenantId);
  const kycQuery = useCACKycChecks(tenantId);
  const transitionsQuery = useCACTierTransitions(tenantId);
  const updateMutation = useCACUpdatePartnerProfile(tenantId);
  const upgradeMutation = useCACRequestTierUpgrade(tenantId);
  const submitEvidence = useCACSubmitKycEvidence(tenantId);

  const profile = profileQuery.data;
  const checks = kycQuery.data ?? [];
  const transitions = transitionsQuery.data ?? [];
  const actorId = currentTenant?.slug || 'tenant-admin';

  if (profileQuery.isLoading) {
    return (
      <div className="flex justify-center py-16" data-testid="partner-profile-page">
        <Spinner />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="space-y-6" data-testid="partner-profile-page">
        <Card className="p-8 text-center text-muted-foreground">
          No partner profile found for this tenant. Complete partner onboarding to create one.
        </Card>
      </div>
    );
  }

  const handleEdit = () => {
    setEditName(profile.display_name);
    setEditEmail(profile.contact_email ?? '');
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    await updateMutation.mutateAsync({
      display_name: editName || undefined,
      contact_email: editEmail || undefined,
    });
    setEditMode(false);
  };

  const handleUpgrade = async () => {
    await upgradeMutation.mutateAsync(actorId);
  };

  const handleSubmitEvidence = async () => {
    if (!evidenceModal) return;
    const evidenceData: Record<string, unknown> = { notes: evidenceNotes || undefined };
    if (evidenceModal === 'fiscal_profile') {
      evidenceData.tax_id = evidenceTaxId;
      evidenceData.country_code = evidenceCountry;
    }
    await submitEvidence.mutateAsync({
      checkSlug: evidenceModal,
      payload: {
        evidence_type: evidenceType,
        evidence_data: evidenceData,
        notes: evidenceNotes || undefined,
      },
    });
    setEvidenceModal(null);
    setEvidenceTaxId('');
    setEvidenceCountry('');
    setEvidenceNotes('');
  };

  return (
    <div className="space-y-6" data-testid="partner-profile-page">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Partner Profile</h2>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={TIER_BADGE_CLASS[profile.tier] ?? ''}>
            {profile.tier}
          </Badge>
          <Badge variant="outline">{profile.verification_status}</Badge>
        </div>
      </div>

      {profile.tier !== 'enterprise' && profile.verification_status === 'verified' ? (
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">Tier Upgrade</h3>
              <p className="text-sm text-muted-foreground">
                Request an upgrade to the next partner tier for more marketplace capabilities.
              </p>
            </div>
            <Button variant="default" size="sm" onClick={handleUpgrade} disabled={upgradeMutation.isPending}>
              Request Upgrade
            </Button>
          </div>
        </Card>
      ) : null}

      {(upgradeMutation.isError || updateMutation.isError || submitEvidence.isError) ? (
        <Card className="p-4 border-destructive/30 text-destructive">
          {publicErrorMessage(upgradeMutation.error ?? updateMutation.error ?? submitEvidence.error)}
        </Card>
      ) : null}

      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">Profile Details</h3>
          {!editMode ? (
            <Button variant="outline" size="sm" onClick={handleEdit}>Edit</Button>
          ) : null}
        </div>
        {editMode ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input placeholder="Display name" value={editName} onChange={(e) => setEditName(e.target.value)} />
              <Input placeholder="Contact email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditMode(false)}>Cancel</Button>
              <Button variant="default" size="sm" onClick={handleSaveEdit} disabled={updateMutation.isPending}>
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-muted-foreground text-xs">Display name</p>
              <p>{profile.display_name}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Slug</p>
              <p className="font-mono">{profile.slug}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Contact email</p>
              <p>{profile.contact_email ?? '—'}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs">Tenant</p>
              <p className="font-mono">{profile.tenant_id}</p>
            </div>
          </div>
        )}
      </Card>

      <Card className="p-4">
        <h3 className="font-medium mb-3">KYC Checks</h3>
        {kycQuery.isLoading ? (
          <Spinner />
        ) : checks.length === 0 ? (
          <p className="text-sm text-muted-foreground">No KYC checks yet.</p>
        ) : (
          <div className="space-y-3">
            {checks.map((check: KycCheck) => (
              <div key={check.id} className="flex items-center justify-between border rounded-md p-3">
                <div>
                  <p className="font-medium text-sm">{KYC_SLUG_LABELS[check.check_slug] ?? check.check_slug}</p>
                  <p className="text-xs text-muted-foreground">
                    {check.expires_at ? `Expires ${new Date(check.expires_at).toLocaleDateString()}` : 'No expiry'}
                  </p>
                  {check.review_notes ? (
                    <p className="text-xs text-muted-foreground mt-1">{check.review_notes}</p>
                  ) : null}
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={KYC_STATUS_BADGE_CLASS[check.status] ?? ''}>
                    {check.status}
                  </Badge>
                  {(check.status === 'pending' || check.status === 'rejected') ? (
                    <Button variant="outline" size="sm" onClick={() => setEvidenceModal(check.check_slug)}>
                      Submit Evidence
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {transitions.length > 0 ? (
        <Card className="p-4">
          <h3 className="font-medium mb-3">Tier History</h3>
          <div className="space-y-2">
            {transitions.map((tr) => (
              <div key={tr.id} className="flex items-center justify-between text-sm">
                <div>
                  <span className="font-mono text-xs">{tr.transition_type}</span>
                  <span className="text-muted-foreground ml-2">
                    {tr.from_tier ?? '—'} → {tr.to_tier}
                  </span>
                </div>
                <Badge variant="outline">{tr.status}</Badge>
              </div>
            ))}
          </div>
        </Card>
      ) : null}

      {evidenceModal ? (
        <AppModal onClose={() => setEvidenceModal(null)} testId="cac-kyc-evidence-modal">
          <Card className="w-full max-w-md p-6 space-y-4 m-4">
            <h3 className="font-semibold">Submit Evidence — {KYC_SLUG_LABELS[evidenceModal] ?? evidenceModal}</h3>
            {evidenceModal === 'fiscal_profile' ? (
              <div className="space-y-3">
                <Input
                  placeholder="Tax ID (e.g. ESB12345678)"
                  value={evidenceTaxId}
                  onChange={(e) => setEvidenceTaxId(e.target.value)}
                />
                <Input
                  placeholder="Country code (e.g. ES)"
                  value={evidenceCountry}
                  onChange={(e) => setEvidenceCountry(e.target.value)}
                />
              </div>
            ) : null}
            <Input
              placeholder="Evidence type (e.g. document)"
              value={evidenceType}
              onChange={(e) => setEvidenceType(e.target.value)}
            />
            <textarea
              className="w-full border border-gray-200 dark:border-gray-700 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-900 min-h-[80px]"
              placeholder="Notes (optional)"
              value={evidenceNotes}
              onChange={(e) => setEvidenceNotes(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setEvidenceModal(null)}>Cancel</Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleSubmitEvidence}
                disabled={submitEvidence.isPending}
              >
                Submit
              </Button>
            </div>
          </Card>
        </AppModal>
      ) : null}
    </div>
  );
}